import { useState, useEffect } from "react";
import axios from "axios";
import { useGlobalContext } from "../../GlobalContext";
import { BarChart2, Loader } from "lucide-react";
import TablePagination from "../Utils/TablePagination";
import Logo from "../../Images/Packky.svg";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import LoadingScreen from "../Utils/LoadingScreen";
import useDriverAllApi from "../../Sevices/Driver Api/useDriverAllApi";

const Settlement = () => {
  const { token } =
    useGlobalContext();

const {
    drivers,
    loading,
    fetchDrivers,
  } = useDriverAllApi();
  const navigate = useNavigate();

  /* States */

  const [loadingDrivers, setLoadingDrivers] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);

  const [search, setSearch] = useState("");

  /* Pagination */
  const driversPerPage = 25;
  const [currentPage, setCurrentPage] = useState(1);

  /* Initial Load */
  useEffect(() => {
    if (token) {
      fetchDrivers();
    }
  }, [token]);

  /* ---------------- FILTERING ---------------- */
  const filteredDrivers = drivers?.filter((driver) => {
    if (!search) return true;
    const q = search.toLowerCase();

    return (
      driver.fullName?.toLowerCase().includes(q) ||
      driver.username?.toLowerCase().includes(q) ||
      driver.driverId?.toLowerCase().includes(q) ||
      driver.email?.toLowerCase().includes(q) ||
      driver.phone?.toLowerCase().includes(q)
    );
  });

  const indexOfLast = currentPage * driversPerPage;
  const indexOfFirst = indexOfLast - driversPerPage;
  const currentDrivers = filteredDrivers?.slice(indexOfFirst, indexOfLast);

  /* ---------------- NAVIGATION ---------------- */
  const handleOpen = () => {
    if (!selectedDriver || !pendingAction) return;

    const driverId = selectedDriver.driverId;
    const username = encodeURIComponent(
      selectedDriver.username || selectedDriver.fullName,
    );

    if (pendingAction === "settlement") {
      navigate(`/settlement-detail/${driverId}/${username}`);
    }

    setConfirmOpen(false);
    setPendingAction(null);
    setSelectedDriver(null);
  };

  const renderPagination = () => {
    const totalCount = filteredDrivers?.length || 0;
    if (totalCount <= driversPerPage) return null;

    return (
      <TablePagination
        currentPage={currentPage}
        totalCount={totalCount}
        pageSize={driversPerPage}
        onPageChange={setCurrentPage}
      />
    );
  };

  /* ---------------- UI ---------------- */
  return (
    <>
      <div className="p-6 bg-gray-50 min-h-screen space-y-6">
        {/* Header */}
        <div className="flex items-center">
          <img src={Logo} alt="Logo" className="h-10" />
          <p className="text-2xl font-bold ps-3">Settlement Report</p>
        </div>

        {/* Search */}
        <input
          type="text"
          placeholder="Search by name, code, email, phone..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setCurrentPage(1);
          }}
          className="px-3 py-2 border rounded text-sm w-full md:w-64"
        />

        {/* Table */}
        {loadingDrivers ? (
          <LoadingScreen />
        ) : (
          <div className="overflow-x-auto border rounded-md shadow-sm">
            <table className="min-w-full bg-white">
              <thead>
                <tr className="bg-orange-400 text-white text-sm">
                  <th className="px-4 py-2 text-center">S/N</th>
                  <th className="px-4 py-2 text-center">Driver ID</th>
                  <th className="px-4 py-2 text-center">User Name</th>
                  <th className="px-4 py-2 text-center">Name</th>
                  <th className="px-4 py-2 text-center">Email</th>
                  <th className="px-4 py-2 text-center">Phone</th>
                  <th className="px-4 py-2 text-center">Action</th>
                </tr>
              </thead>

              <tbody>
                {loadingDrivers ? (
                  <tr>
                    <td colSpan="7" className="py-6 text-center">
                      <div className="flex justify-center gap-2">
                        <Loader className="animate-spin w-5 h-5 text-blue-600" />
                        Loading drivers...
                      </div>
                    </td>
                  </tr>
                ) : currentDrivers?.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="py-6 text-center text-gray-500">
                      No drivers found
                    </td>
                  </tr>
                ) : (
                  currentDrivers.map((driver, index) => (
                    <tr key={driver.id} className="border-t text-sm">
                      <td className="px-4 py-2 text-center">
                        {indexOfFirst + index + 1}
                      </td>
                      <td className="px-4 py-2 text-center">
                        {driver.driverId}
                      </td>
                      <td className="px-4 py-2 text-center">
                        {driver.username}
                      </td>
                      <td className="px-4 py-2 text-center font-medium">
                        {driver.fullName}
                      </td>
                      <td className="px-4 py-2 text-center">{driver.email}</td>
                      <td className="px-4 py-2 text-center">{driver.phone}</td>
                      <td className="px-4 py-2 text-center">
                        <button
                          className="inline-flex items-center gap-1 rounded-xl border px-2.5 py-1.5 text-xs hover:bg-gray-50"
                          onClick={() => {
                            setSelectedDriver(driver);
                            setPendingAction("settlement");
                            setConfirmOpen(true);
                          }}
                        >
                          <BarChart2 size={14} /> Settlement
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
        {renderPagination()}
      </div>

      {/* Confirm Modal */}
      {confirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-lg w-[90%] max-w-sm p-5">
            <h3 className="text-lg font-semibold mb-2">
              Open Driver Settlement Report
            </h3>

            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to open settlement report for{" "}
              <span className="font-medium">{selectedDriver?.fullName}</span>?
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setConfirmOpen(false)}
                className="px-4 py-2 text-sm rounded border"
              >
                Cancel
              </button>
              <button
                onClick={handleOpen}
                className="px-4 py-2 text-sm rounded bg-blue-600 text-white"
              >
                Open
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Settlement;
